'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import { tv } from 'tailwind-variants';

import { Label } from './label.component';

import { cn } from '@/utils/cn-tw-merge';

const checkboxStyles = tv({
  slots: {
    container: [
      'peer h-6 w-6 shrink-0 rounded-sm border border-gray-400 ring-offset-background focus-visible:outline-none',
      'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed',
      'disabled:opacity-50 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500',
      'data-[state=checked]:text-primary-foreground',
    ],
    label: 'text-base text-gray-700 font-semibold cursor-pointer',
  },

  variants: {
    checked: {
      true: {
        label: 'font-semibold',
      },

      indeterminate: {},
    },
  },
});

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    htmlFor?: string;
  }
>(({ className, htmlFor, ...props }, ref) => {
  const { checked } = props;
  const { container, label } = checkboxStyles();

  return (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 min-w-12 min-h-12 flex items-center justify-center bg-green-50 rounded-lg">
        <CheckboxPrimitive.Root ref={ref} id={htmlFor} className={container({ className })} {...props}>
          <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
            <Check className="h-4 w-4" />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
      </div>

      <Label htmlFor={htmlFor} className={label({ checked })}>
        {props.children}
      </Label>
    </div>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
